import React, { useState } from 'react';
import {
  CheckCircle, Warning, XCircle, MinusCircle, Gauge,
  Buildings, ShieldCheck, Factory, FilePdf, CaretDown, CaretRight,
  Lightning, Users, Package, Scales, Fire, Leaf, Recycle, CurrencyEur
} from '@phosphor-icons/react';
import { getCbamDeclarationPack } from '../api';
import { exportSupplierCommPack } from '../utils/exportSupplierPack';

const STATUS_CONFIG = {
  complete: { icon: CheckCircle, cls: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Complete' },
  partial: { icon: Warning, cls: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Partial' },
  missing: { icon: XCircle, cls: 'text-red-400', bg: 'bg-red-500/10', label: 'Missing' },
  not_applicable: { icon: MinusCircle, cls: 'text-slate-400', bg: 'bg-slate-500/10', label: 'N/A' },
};

const CATEGORY_ICON = {
  'Installation ID': Buildings,
  'Direct Emissions': Fire,
  'Indirect Emissions': Leaf,
  'Precursor Emissions': Package,
  'Production Data': Factory,
  'Carbon Price': CurrencyEur,
  'Verification': ShieldCheck,
  'Methodology': Scales,
};

const URGENCY_STYLE = {
  high: { cls: 'text-red-400 bg-red-500/10 border-red-500/30', label: 'High' },
  medium: { cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30', label: 'Medium' },
  low: { cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', label: 'Low' },
};

function ReadinessGauge({ pct, size = 'lg' }) {
  const color = pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400';
  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const textSize = size === 'lg' ? 'text-3xl' : 'text-xl';
  return (
    <div className="text-center">
      <p className={`font-heading font-bold ${textSize} ${color}`}>{pct}%</p>
      <div className="w-full bg-white/5 rounded-full h-2 mt-2">
        <div className={`h-2 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.missing;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.cls}`}>
      <Icon size={10} weight="fill" />{cfg.label}
    </span>
  );
}

export default function CbamSupplierCompliance({ data }) {
  const [expandedInst, setExpandedInst] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});
  const [exportingPack, setExportingPack] = useState(null);

  if (!data) return null;
  const { buyer_requests, installation_checklists, overall_readiness_pct, total_buyers, total_installations } = data;

  const toggleInst = (id) => setExpandedInst(expandedInst === id ? null : id);
  const toggleCat = (instId, cat) => {
    const key = `${instId}-${cat}`;
    setExpandedCats(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleExportPack = async (inst) => {
    setExportingPack(inst.installation_id);
    try {
      await exportSupplierCommPack(inst, data);
    } catch (e) {
      console.error('Pack export failed', e);
    } finally {
      setExportingPack(null);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="cbam-supplier-compliance">
      {/* Overall Readiness Header */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface border border-border rounded-lg p-5 flex flex-col items-center justify-center" data-testid="cbam-overall-readiness">
          <Gauge size={24} weight="duotone" className="text-primary mb-2" />
          <p className="text-[11px] font-body font-semibold uppercase tracking-wider text-text-secondary mb-2">Overall Readiness</p>
          <ReadinessGauge pct={overall_readiness_pct} />
        </div>
        <div className="bg-surface border border-border rounded-lg p-5 text-center">
          <Users size={22} weight="duotone" className="text-blue-400 mx-auto mb-2" />
          <p className="text-2xl font-heading font-bold text-text-primary">{total_buyers}</p>
          <p className="text-[11px] font-body text-text-secondary uppercase tracking-wider mt-1">EU Buyers Requesting Data</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-5 text-center">
          <Factory size={22} weight="duotone" className="text-teal-400 mx-auto mb-2" />
          <p className="text-2xl font-heading font-bold text-text-primary">{total_installations}</p>
          <p className="text-[11px] font-body text-text-secondary uppercase tracking-wider mt-1">Installations to Report</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-5 text-center">
          <Lightning size={22} weight="duotone" className="text-amber-400 mx-auto mb-2" />
          <p className="text-2xl font-heading font-bold text-text-primary">
            {installation_checklists.filter(c => c.pack_generation.ready).length}/{total_installations}
          </p>
          <p className="text-[11px] font-body text-text-secondary uppercase tracking-wider mt-1">Packs Ready to Generate</p>
        </div>
      </div>

      {/* Buyer Request Dashboard */}
      <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-buyer-requests">
        <h3 className="text-sm font-body font-semibold text-text-primary mb-1">EU Buyer Data Requests</h3>
        <p className="text-[11px] font-body text-text-secondary mb-4">What your EU customers need from you for their CBAM declarations</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-body">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-text-secondary font-semibold">EU Importer</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Country</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Products Requested</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Shipments</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Volume</th>
                <th className="text-right py-2 text-text-secondary font-semibold">CBAM Exposure</th>
                <th className="text-center py-2 text-text-secondary font-semibold">Urgency</th>
              </tr>
            </thead>
            <tbody>
              {(buyer_requests || []).map((b, i) => {
                const urg = URGENCY_STYLE[b.urgency] || URGENCY_STYLE.low;
                return (
                  <tr key={i} className="border-b border-border/50 hover:bg-white/[0.02]" data-testid={`cbam-buyer-${i}`}>
                    <td className="py-2.5 text-text-primary font-medium">{b.importer}</td>
                    <td className="py-2.5 text-text-secondary">{b.country}</td>
                    <td className="py-2.5 text-text-secondary max-w-[200px]">
                      <div className="flex flex-wrap gap-1">
                        {(b.products_requested || []).map((p, pi) => (
                          <span key={pi} className="text-[10px] bg-white/5 border border-border px-1.5 py-0.5 rounded">{p.length > 20 ? p.slice(0, 20) + '...' : p}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2.5 text-right text-text-primary font-semibold">{b.shipment_count}</td>
                    <td className="py-2.5 text-right text-text-secondary">{b.total_tonnes}t</td>
                    <td className="py-2.5 text-right font-semibold text-amber-400">EUR {b.total_net_eur?.toLocaleString()}</td>
                    <td className="py-2.5 text-center">
                      <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${urg.cls}`}>{urg.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Installation Checklists */}
      <div className="space-y-3" data-testid="cbam-installation-checklists">
        <h3 className="text-sm font-body font-semibold text-text-primary">Compliance Checklist by Installation</h3>
        {(installation_checklists || []).map((inst) => {
          const isExpanded = expandedInst === inst.installation_id;
          const r = inst.readiness;
          const categories = [...new Set(inst.checklist.map(c => c.category))];

          return (
            <div key={inst.installation_id} className="bg-surface border border-border rounded-lg overflow-hidden" data-testid={`cbam-checklist-${inst.installation_id}`}>
              {/* Installation Header */}
              <button
                onClick={() => toggleInst(inst.installation_id)}
                className="w-full flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors"
                data-testid={`cbam-checklist-toggle-${inst.installation_id}`}
              >
                {isExpanded ? <CaretDown size={16} className="text-text-secondary shrink-0" /> : <CaretRight size={16} className="text-text-secondary shrink-0" />}
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-body font-semibold text-text-primary">{inst.site_name}</span>
                    <span className="text-[10px] font-body text-text-secondary">({inst.country} — {inst.operator_entity})</span>
                    {inst.verification_status === 'Verified' ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full"><CheckCircle size={10} weight="fill" />Verified</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full"><Warning size={10} weight="fill" />Pending</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-[11px] font-body text-text-secondary">
                    <span>{inst.products.length} product{inst.products.length !== 1 ? 's' : ''}</span>
                    <span className="text-emerald-400">{r.complete} complete</span>
                    <span className="text-amber-400">{r.partial} partial</span>
                    <span className="text-red-400">{r.missing} missing</span>
                  </div>
                </div>
                <div className="w-28 shrink-0">
                  <ReadinessGauge pct={r.pct} size="sm" />
                </div>
              </button>

              {/* Expanded Checklist */}
              {isExpanded && (
                <div className="border-t border-border p-4 space-y-4">
                  {/* Pack Generation Status */}
                  <div className={`rounded-lg p-4 border ${inst.pack_generation.ready ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-amber-500/5 border-amber-500/30'}`} data-testid={`cbam-pack-status-${inst.installation_id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-body font-semibold text-text-primary">Supplier Communication Pack</p>
                        <p className="text-xs font-body text-text-secondary mt-0.5">
                          Estimated generation time: <span className="font-semibold text-text-primary">{inst.pack_generation.estimated_time}</span>
                        </p>
                        <p className="text-[11px] font-body text-text-secondary mt-0.5">{inst.pack_generation.note}</p>
                      </div>
                      <button
                        data-testid={`cbam-generate-pack-${inst.installation_id}`}
                        onClick={() => handleExportPack(inst)}
                        disabled={exportingPack === inst.installation_id}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-body font-semibold transition-colors ${
                          inst.pack_generation.ready
                            ? 'bg-primary text-white hover:bg-primary/90'
                            : 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                        } disabled:opacity-50`}
                      >
                        <FilePdf size={16} weight="duotone" />
                        {exportingPack === inst.installation_id ? 'Generating...' : 'Generate Pack'}
                      </button>
                    </div>
                  </div>

                  {/* Products at this installation */}
                  <div>
                    <p className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider mb-2">Products at this Installation</p>
                    <div className="flex flex-wrap gap-2">
                      {inst.products.map((p, pi) => (
                        <span key={pi} className="text-xs font-body text-text-primary bg-white/5 border border-border px-3 py-1.5 rounded-lg">
                          {p.description} <span className="text-text-secondary font-mono text-[10px] ml-1">{p.cn_code}</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Checklist by Category */}
                  <div className="space-y-2">
                    {categories.map((cat) => {
                      const catKey = `${inst.installation_id}-${cat}`;
                      const catExpanded = expandedCats[catKey] !== false; // default open
                      const catItems = inst.checklist.filter(c => c.category === cat);
                      const CatIcon = CATEGORY_ICON[cat] || Scales;
                      const catComplete = catItems.filter(c => c.status === 'complete').length;
                      const catTotal = catItems.filter(c => c.status !== 'not_applicable').length;

                      return (
                        <div key={catKey} className="border border-border rounded-lg overflow-hidden">
                          <button
                            onClick={() => toggleCat(inst.installation_id, cat)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] transition-colors"
                          >
                            {catExpanded ? <CaretDown size={12} className="text-text-secondary" /> : <CaretRight size={12} className="text-text-secondary" />}
                            <CatIcon size={14} weight="duotone" className="text-tertiary" />
                            <span className="text-xs font-body font-semibold text-text-primary flex-1 text-left">{cat}</span>
                            <span className={`text-[10px] font-semibold ${catComplete === catTotal ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {catComplete}/{catTotal}
                            </span>
                          </button>
                          {catExpanded && (
                            <div className="border-t border-border/50">
                              {catItems.map((item, ii) => (
                                <div key={ii} className="flex items-start gap-3 px-4 py-2.5 border-b border-border/30 last:border-0 hover:bg-white/[0.01]" data-testid={`cbam-check-${item.id}`}>
                                  <div className="mt-0.5 shrink-0"><StatusBadge status={item.status} /></div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-body text-text-primary">{item.requirement}</p>
                                    <p className="text-[10px] font-body text-text-secondary mt-0.5">{item.notes}</p>
                                  </div>
                                  <span className="text-[9px] font-mono text-text-secondary/60 shrink-0 mt-0.5">{item.regulation_ref}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
